import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import { blogService } from '../lib/database';
import type { BlogPost } from '../lib/supabase';

const BlogSection = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const data = await blogService.getFeatured(3);
      setBlogPosts(data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (blogPosts.length === 0) {
    return (
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold text-dark mb-3">
              Latest Blog Posts & Tips
            </h2>
            <p className="lead text-muted">
              Stay updated with the latest college admission insights
            </p>
          </div>
          <div className="text-center">
            <p className="text-muted">No blog posts available yet.</p>
            <Link to="/blog" className="btn btn-primary">
              View All Articles
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-3 py-md-5 bg-light">
      <div className="container">
        <div className="text-center mb-4 mb-md-5 px-2">
          <h2 className="h1 fw-bold text-dark mb-3" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>
            Latest Blog Posts & Tips
          </h2>
          <p className="lead text-muted" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>
            Stay updated with the latest college admission insights
          </p>
        </div>

        <div className="row g-2 g-md-4 px-2 px-md-0">
          {blogPosts.map((post) => (
            <div key={post.id} className="col-12 col-sm-6 col-lg-4">
              <div className="blog-card card h-100 border-0 shadow-sm">
                <div className="position-relative">
                  <OptimizedImage
                    src={post.image_url}
                    alt={`${post.title} - ${post.category} guidance article by ${post.author}`}
                    title={`${post.title} by ${post.author} | ${post.read_time}`}
                    className="card-img-top"
                    style={{ height: 'clamp(140px, 20vw, 200px)', objectFit: 'cover' }}
                    loading="lazy"
                    fallbackSrc="https://images.pexels.com/photos/5212324/pexels-photo-5212324.jpeg?auto=compress&cs=tinysrgb&w=400"
                  />
                  <span className="category-badge position-absolute top-0 start-0 m-3">
                    {post.category}
                  </span>
                </div>

                <div className="card-body p-2 p-md-4">
                  <h5 className="card-title fw-bold text-dark mb-3" style={{ fontSize: 'clamp(1rem, 3.5vw, 1.25rem)' }}>
                    {post.title}
                  </h5>
                  
                  <p className="card-text text-muted line-clamp-3 mb-3 mb-md-4" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)' }}>
                    {post.excerpt}
                  </p>

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                      <Clock size={14} />
                      <span>{post.read_time}</span>
                    </div>
                    
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="btn btn-link p-0 text-primary fw-semibold d-flex align-items-center gap-2"
                      style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)' }}
                      aria-label={`Read full article: ${post.title}`}
                    >
                      <span>Read More</span>
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-4 mt-md-5 px-2">
          <Link 
            to="/blog" 
            className="btn btn-primary btn-lg w-100 w-sm-auto"
            style={{ minHeight: '48px', maxWidth: '300px' }}
            aria-label="View all blog articles and guidance content"
          >
            View All Articles
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;